/**
 * ReplayPage — Replay recorded browser sessions.
 *
 * Two modes:
 * - Steps (L2): Replays stored steps mechanically, no LLM
 * - Agent (L1): Re-runs task with LLM against cached/live pages
 *
 * Desktop: Three-panel layout — RecordingList | Browser Stream | Controls + Timeline
 * Mobile:  Vertical stack — Browser Stream top, Controls + Timeline bottom, collapsible sidebar
 *
 * Route: /replay
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { playgroundService } from '../services/playgroundApi';
import { usePlaygroundSSE } from '../hooks/usePlaygroundSSE';
import { RecordingList } from '../components/replay/RecordingList';
import { ReplayControls } from '../components/replay/ReplayControls';
import { StepTimeline } from '../components/replay/StepTimeline';
import { EventStream } from '../components/playground/EventStream';
import { TopNav } from '../components/ui/TopNav';
import { StepSequence, StepSequenceStep, StepReplayProgress } from '../types/playground';
import toast from 'react-hot-toast';

export const ReplayPage: React.FC = () => {
  // Selection state
  const [selectedSequenceId, setSelectedSequenceId] = useState<string | null>(null);
  const [selectedSequence, setSelectedSequence] = useState<(StepSequence & { steps: StepSequenceStep[] }) | null>(null);
  const [isLoadingSequence, setIsLoadingSequence] = useState(false);

  // Controls state
  const [mode, setMode] = useState<'steps' | 'agent'>('steps');
  const [source, setSource] = useState<'cached' | 'live' | 'hybrid'>('cached');
  const [replayGateway, setReplayGateway] = useState<string>('default');
  const [taskText, setTaskText] = useState('');

  // Session state
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [stepProgress, setStepProgress] = useState<StepReplayProgress[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  // Layout state
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Track mobile state
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setIsSidebarOpen(false);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch full sequence when selection changes
  useEffect(() => {
    if (!selectedSequenceId) {
      setSelectedSequence(null);
      setStepProgress([]);
      return;
    }

    let cancelled = false;
    setIsLoadingSequence(true);

    playgroundService
      .getStepSequence(selectedSequenceId)
      .then((seq) => {
        if (!cancelled) {
          setSelectedSequence(seq);
          setTaskText(seq.name || '');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err instanceof Error ? err.message : 'Failed to load recording');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingSequence(false);
          // Auto-collapse sidebar on mobile after selection
          if (isMobile) setIsSidebarOpen(false);
        }
      });

    return () => { cancelled = true; };
  }, [selectedSequenceId, isMobile]);

  // SSE connection
  const {
    events: sseEvents,
    isRunning,
    streamingUrl,
  } = usePlaygroundSSE(
    activeSessionId || undefined,
    !!activeSessionId
  );

  // Extract step_replay_progress events from SSE
  useEffect(() => {
    const progressEvents: StepReplayProgress[] = [];
    for (const event of sseEvents) {
      if (event.type === 'step_replay_progress') {
        const data = event.data || event;
        progressEvents.push({
          type: 'step_replay_progress',
          step_index: data.step_index,
          step_total: data.step_total,
          slug: data.slug,
          action: data.action,
          status: data.status,
          duration_ms: data.duration_ms,
          reason: data.reason,
        });
      }
    }
    setStepProgress(progressEvents);
  }, [sseEvents]);

  // Poll session status to detect completion
  useEffect(() => {
    if (!activeSessionId) return;

    let alive = true;
    const checkStatus = async () => {
      try {
        const session = await playgroundService.getSessionDetails(activeSessionId);
        if (alive && (session.status === 'completed' || session.status === 'failed')) {
          setIsCreating(false);
          setActiveSessionId(null);
        }
      } catch {
        if (alive) {
          setIsCreating(false);
          setActiveSessionId(null);
        }
      }
    };

    // Check immediately after 1s (give SSE a chance to connect first)
    const initialTimeout = setTimeout(checkStatus, 1000);
    // Then poll every 2s
    const interval = setInterval(checkStatus, 2000);

    return () => {
      alive = false;
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [activeSessionId]);

  // Clear isCreating when SSE connects
  useEffect(() => {
    if (isRunning && isCreating) {
      setIsCreating(false);
    }
  }, [isRunning, isCreating]);

  const handleReplay = useCallback(async () => {
    if (!selectedSequenceId || !selectedSequence) return;

    setIsCreating(true);
    setStepProgress([]);

    try {
      const gwParam = replayGateway !== 'default' ? replayGateway : undefined;
      if (mode === 'steps') {
        const session = await playgroundService.createSession({
          user_message: taskText || 'Step replay',
          browser_type: 'stealth',
          use_proxy: false,
          step_replay_mode: `replay-${source}`,
          step_sequence_id: selectedSequenceId,
          gateway: gwParam,
        });
        setActiveSessionId(session.session_id);
      } else {
        const session = await playgroundService.createSession({
          user_message: taskText || selectedSequence.name || 'Agent replay',
          browser_type: 'stealth',
          use_proxy: false,
          tinyctx_mode: source === 'cached' ? 'replay' : source === 'hybrid' ? 'hybrid' : 'live',
          recording_id: source !== 'live' ? selectedSequence.recording_id : undefined,
          gateway: gwParam,
        });
        setActiveSessionId(session.session_id);
      }
      // Auto-collapse sidebar on mobile when running
      if (isMobile) setIsSidebarOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to start replay');
      setIsCreating(false);
    }
  }, [selectedSequenceId, selectedSequence, mode, source, taskText, isMobile]);

  const [isStopping, setIsStopping] = useState(false);

  const handleStop = useCallback(async () => {
    if (!activeSessionId) return;
    setIsStopping(true);
    try {
      await playgroundService.stopSession(activeSessionId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to stop session');
    }
    setActiveSessionId(null);
    setIsCreating(false);
    setIsStopping(false);
  }, [activeSessionId]);

  const handleSelectRecording = useCallback((id: string) => {
    setSelectedSequenceId(id);
    setActiveSessionId(null);
    setStepProgress([]);
  }, []);

  const timelineSteps = useMemo(() => {
    if (!selectedSequence?.steps) return [];
    return selectedSequence.steps.map((s) => ({
      index: s.index,
      action: s.action,
      slug: s.slug,
      value: s.value ?? undefined,
    }));
  }, [selectedSequence]);

  const sessionIsActive = isRunning || isCreating || isStopping;

  // Selected recording label for mobile header
  const selectedLabel = selectedSequence
    ? `${selectedSequence.domain} · ${selectedSequence.step_count} steps`
    : 'Select recording';

  // Browser stream content (shared between mobile and desktop)
  const streamContent = (
    <>
      {isCreating && !streamingUrl ? (
        <div className="flex items-center justify-center h-full bg-white">
          <div className="text-center text-gray-400">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
            <p className="text-sm">Initializing browser...</p>
          </div>
        </div>
      ) : streamingUrl && activeSessionId ? (
        <iframe
          src={streamingUrl}
          className="w-full h-full border-0 pointer-events-none"
          title="Browser streaming"
          sandbox="allow-same-origin allow-scripts"
        />
      ) : selectedSequence ? (
        <div className="flex items-center justify-center h-full bg-white">
          <div className="text-center text-gray-400">
            <p className="text-sm font-medium text-gray-600">{selectedSequence.domain}</p>
            <p className="text-xs mt-1 max-w-[200px] truncate">{selectedSequence.name}</p>
            <p className="text-xs mt-2">Press Replay to start</p>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center h-full bg-white">
          <div className="text-center text-gray-400">
            <p className="text-sm">Select a recording</p>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="h-[100dvh] flex flex-col overflow-hidden">
      <TopNav />
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden bg-gray-50">

        {/* Sidebar: collapsible overlay on mobile, side panel on desktop */}
        {isSidebarOpen && (
          <div className={`${isMobile ? 'absolute inset-x-0 top-16 bottom-0 z-40 bg-white' : 'relative'} flex flex-col border-r border-gray-200`}>
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 flex-shrink-0">
              <span className="text-sm font-medium text-gray-700">Recordings</span>
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
                title="Collapse"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <RecordingList
              selectedId={selectedSequenceId}
              onSelect={handleSelectRecording}
            />
          </div>
        )}

        {/* Main content */}
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden">

          {/* Center: Browser Stream */}
          <div className="w-full h-[40vh] shrink-0 md:shrink md:h-full md:flex-1 bg-white overflow-hidden flex flex-col">
            {/* Top bar: recording selector + source toggle */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 bg-gray-50 flex-shrink-0">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="px-2 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 truncate max-w-[240px]"
              >
                {selectedLabel}
              </button>
              <div className="flex gap-1 ml-auto flex-shrink-0 items-center">
                {(['cached', 'live', 'hybrid'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setSource(s)}
                    disabled={sessionIsActive}
                    className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${
                      source === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            {streamContent}
          </div>

          {/* Right Panel: Controls + Timeline/Events */}
          <div className="w-full flex-1 min-h-0 md:flex-none md:h-full md:w-80 bg-white flex flex-col overflow-hidden border-t md:border-t-0 md:border-l border-gray-200">
            {/* Controls */}
            <div className="flex-shrink-0 px-4 py-3 border-b border-gray-100">
              <ReplayControls
                mode={mode}
                isRunning={sessionIsActive}
                isStopping={isStopping}
                taskText={taskText}
                onModeChange={setMode}
                onReplay={handleReplay}
                onStop={handleStop}
                onTaskTextChange={setTaskText}
                disabled={!selectedSequenceId || isLoadingSequence}
              />
            </div>

            {/* Timeline or Events */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              {isLoadingSequence ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                </div>
              ) : mode === 'steps' ? (
                <StepTimeline
                  steps={timelineSteps}
                  progress={stepProgress}
                />
              ) : (
                <EventStream
                  events={sseEvents.filter(e => e.type !== 'step_replay_progress')}
                  autoScroll={isRunning}
                  alwaysExpanded={true}
                  endTime={!sessionIsActive && sseEvents.length > 0 ? new Date().toISOString() : undefined}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
